import React from 'react';
import {
  Users, Eye, MapPin, BarChart3, Target, CheckCircle, FileText,
  TrendingUp, Info, XCircle, ArrowDownRight,
  PieChart as PieChartIcon
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const STATUS_REMARK_MAPPING = {
  INPROCESS: [
    "CALL BACK LATER", "OFFICE VISIT SCHEDULE", "OFFICE VISIT DONE",
    "PROJECT VISIT SCHEDULE", "PROJECT VISIT DONE", "ASK PROJECT DETAILS"
  ],
  HOLD: ["HIGH RATE", "INSTALLMENT", "LONG DISTANCE", "NA PLOT", "NO DEVELOPMENT", "NO MONEY"],
  CANCEL: ["CP WORKING", "NOT INTERESTED"],
  DONE: [
    "BOOKING", "BOOKING INSTALLMENTS", "KHAREDIKHAT INPROCESS",
    "KHAREDIKHAT DONE", "7/12 INPROCESS", "7/12 DONE", "POSSESSION"
  ]
};

const OVERVIEW_PROJECTS = [
  { id: 'all', label: 'All Projects', projectKeys: [] },
  { id: 'dream_kokan', label: 'Dream Kokan City', projectKeys: ['dream_kokan'] },
  { id: 'dream_nagpur', label: 'Dream Nagpur City', projectKeys: ['dream_nagpur'] },
  { id: 'siddhivinayak', label: 'Siddhivinayak Infra', projectKeys: ['brahma_heights', 'godavari_heights', 'siddhivinayak_heights', 'kaveri_heights'] },
  { id: 'brahma_heights', label: 'Brahma Heights', projectKeys: ['brahma_heights'], parent: 'siddhivinayak' },
  { id: 'godavari_heights', label: 'Godavari Heights', projectKeys: ['godavari_heights'], parent: 'siddhivinayak' },
  { id: 'siddhivinayak_heights', label: 'Siddhivinayak Heights', projectKeys: ['siddhivinayak_heights'], parent: 'siddhivinayak' },
  { id: 'kaveri_heights', label: 'Kaveri Heights', projectKeys: ['kaveri_heights'], parent: 'siddhivinayak' },
  { id: 'dream_riviera_a', label: 'Dream Riviera A Wing', projectKeys: ['dream_riviera_a'] },
  { id: 'dream_riviera_b', label: 'Dream Riviera B Wing', projectKeys: ['dream_riviera_b'] },
  { id: 'sai_plaza', label: 'Sai Plaza', projectKeys: ['sai_plaza'] }
];

const OverviewTab = ({ overviewProject, overviewStats }) => {
  const currentOp = OVERVIEW_PROJECTS.find(p => p.id === overviewProject) || OVERVIEW_PROJECTS[0];

  const ovStatusCounts = overviewStats.statusCounts || { INPROCESS: 0, HOLD: 0, CANCEL: 0, DONE: 0 };
  const ovRemarkCounts = overviewStats.remarkCounts || {};
  const ovStageCounts = overviewStats.stageCounts || {};
  const ovTotalLeads = overviewStats.totalLeads || 0;
  const kpi = overviewStats.kpiMetrics || {};

  const statusChartData = Object.entries(ovStatusCounts).map(([name, value]) => ({ name, value: value || 0 }));
  const STATUS_COLORS_CHART = { INPROCESS: '#b45309', HOLD: '#0284c7', CANCEL: '#b91c1c', DONE: '#15803d' };

  const statusWiseRemarkData = [];
  Object.entries(STATUS_REMARK_MAPPING).forEach(([status, remarks]) => {
    remarks.forEach(remark => {
      const count = ovRemarkCounts[remark] || 0;
      statusWiseRemarkData.push({ remark, status, count, color: STATUS_COLORS_CHART[status] });
    });
  });

  const overallSummaryData = (overviewStats.overallSummary || [])
    .filter(r => r.particular !== 'CONFIRM SALE')
    .map(r => ({ name: r.particular, REQ: r.req, DONE: r.done, BAL: r.bal }));

  const totalLeads = ovTotalLeads || 0;
  const bookings = kpi.bookings || 0;
  const conversionRate = totalLeads > 0 ? ((bookings / totalLeads) * 100).toFixed(2) : 0;
  const officeVisits = kpi.officeVisit || 0;
  const projectVisits = kpi.projectVisit || 0;
  const scope = kpi.scope || 0;
  const confirmSale = kpi.confirmSale || 0;
  const registry = kpi.registry || 0;

  const statusPieData = [
    { name: 'In Process', value: ovStatusCounts.INPROCESS || 0, color: '#f59e0b' },
    { name: 'Cancelled', value: ovStatusCounts.CANCEL || 0, color: '#ef4444' },
    { name: 'Hold', value: ovStatusCounts.HOLD || 0, color: '#3b82f6' },
    { name: 'Done', value: ovStatusCounts.DONE || 0, color: '#22c55e' }
  ];

  const totalStatus = statusPieData.reduce((sum, item) => sum + item.value, 0);
  const statusPercentages = statusPieData.map(item => ({
    ...item,
    percentage: totalStatus > 0 ? ((item.value / totalStatus) * 100).toFixed(1) : 0
  }));

  const ovRatio = totalLeads > 0 ? ((officeVisits / totalLeads) * 100).toFixed(0) : 0;
  const cancelCount = ovStatusCounts.CANCEL || 0;
  const cancelRate = totalLeads > 0 ? ((cancelCount / totalLeads) * 100).toFixed(0) : 0;
  const scopeRate = totalLeads > 0 ? ((scope / totalLeads) * 100).toFixed(1) : 0;
  const convRate = parseFloat(conversionRate);

  const insightLines = [];
  if (ovRatio >= 50) insightLines.push({ icon: TrendingUp, color: 'text-emerald-300', text: `OV ratio ${ovRatio}% — strong funnel` });
  else insightLines.push({ icon: ArrowDownRight, color: 'text-amber-300', text: `OV ratio ${ovRatio}% — improve engagement` });
  if (convRate >= 5) insightLines.push({ icon: TrendingUp, color: 'text-emerald-300', text: `Conversion ${conversionRate}% — above avg` });
  else if (convRate >= 3) insightLines.push({ icon: ArrowDownRight, color: 'text-amber-300', text: `Conversion ${conversionRate}% — below avg` });
  else insightLines.push({ icon: ArrowDownRight, color: 'text-rose-300', text: `Conversion ${conversionRate}% — needs boost` });
  if (cancelRate > 25) insightLines.push({ icon: XCircle, color: 'text-rose-300', text: `Cancel rate ${cancelRate}% — review follow-ups` });
  else if (scopeRate > 5) insightLines.push({ icon: CheckCircle, color: 'text-emerald-300', text: `Scope rate ${scopeRate}% — good pipeline` });
  else insightLines.push({ icon: CheckCircle, color: 'text-blue-300', text: `Scope rate ${scopeRate}% — focus on conversion` });

  let insightTitle = 'Insights';
  if (convRate >= 5 && parseInt(ovRatio) >= 50) insightTitle = 'Strong Progress';
  else if (convRate >= 3 || parseInt(ovRatio) >= 40) insightTitle = 'On Track';
  else if (convRate >= 1) insightTitle = 'Needs Attention';
  else insightTitle = 'Below Target';

  const summaryByParticular = {};
  (overviewStats.overallSummary || []).forEach(r => { summaryByParticular[r.particular] = r; });
  const CARD_PARTICULAR = {
    'leads': 'LEAD', 'office-visits': 'OFFICE VISIT', 'project-visits': 'PROJECT VISIT',
    'scope': 'SCOPE', 'bookings': 'BOOKING', 'confirm-sale': 'CONFIRM SALE',
  };
  const getAchievementBadge = (cardKey) => {
    const particular = CARD_PARTICULAR[cardKey];
    if (!particular) return null;
    const row = summaryByParticular[particular];
    if (!row) return null;
    if (cardKey === 'leads') {
      if (!row.req) return null;
      const pct = ((row.done / row.req) * 100).toFixed(1);
      return { todayVal: row.done, target: row.req, pct, above: row.done >= row.req };
    }
    const leadsDone = (summaryByParticular['LEAD'] || {}).done || 0;
    if (!leadsDone) return null;
    const pct = ((row.done / leadsDone) * 100).toFixed(1);
    return { todayVal: row.done, target: leadsDone, pct, above: true };
  };

  const kpiCards = [
    { key: 'leads', label: 'Leads', value: totalLeads, icon: Users, gradient: 'from-blue-800 to-blue-950', shadow: 'shadow-blue-800/30', sub: 'Total Leads' },
    { key: 'office-visits', label: 'Office Visits', value: officeVisits, icon: Eye, gradient: 'from-cyan-500 to-sky-600', shadow: 'shadow-cyan-500/30', sub: 'Visited Office' },
    { key: 'project-visits', label: 'Project Visits', value: projectVisits, icon: MapPin, gradient: 'from-violet-500 to-purple-600', shadow: 'shadow-violet-500/30', sub: 'Visited Site' },
    { key: 'scope', label: 'Scope', value: scope, icon: BarChart3, gradient: 'from-blue-500 to-blue-600', shadow: 'shadow-blue-500/30', sub: 'Scope of Customer' },
    { key: 'bookings', label: 'Bookings', value: bookings, icon: Target, gradient: 'from-green-500 to-green-600', shadow: 'shadow-green-500/30', sub: 'Booked' },
    { key: 'confirm-sale', label: 'Confirm Sale', value: confirmSale, icon: CheckCircle, gradient: 'from-green-700 to-green-900', shadow: 'shadow-green-700/30', sub: 'Sale Confirmed' },
    { key: 'registry', label: 'Registry', value: registry, icon: FileText, gradient: 'from-yellow-400 to-amber-500', shadow: 'shadow-yellow-400/30', sub: 'Registered' },
    { key: '712', label: '7/12', value: (kpi['712done'] || 0) + (kpi['712inprocess'] || 0), icon: FileText, gradient: 'from-slate-700 to-slate-900', shadow: 'shadow-slate-700/30', sub: '7/12 STATUS', is712: true, ip712: kpi['712inprocess'] || 0, done712: kpi['712done'] || 0 },
    { key: 'conversion-rate', label: 'Conversion Rate', value: `${conversionRate}%`, icon: TrendingUp, gradient: 'from-orange-400 to-rose-500', shadow: 'shadow-orange-400/30', sub: 'Lead → Booking' },
  ];

  return (
    <div className="space-y-6">
      {/* KPI Ribbon */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5" data-testid="overview-kpi-ribbon">
        {kpiCards.map((card) => {
          const Icon = card.icon;
          const isPercent = typeof card.value === 'string';
          const badge = getAchievementBadge(card.key);
          return (
            <Card
              key={card.key}
              className={`bg-gradient-to-br ${card.gradient} border-0 shadow-xl ${card.shadow} hover:scale-[1.02] transition-transform duration-200`}
              data-testid={`kpi-${card.key}`}
            >
              <CardContent className="p-6 min-h-[150px] flex flex-col justify-between relative">
                <div className="flex items-center space-x-2">
                  <div className="p-2.5 bg-white/20 rounded-lg backdrop-blur-sm">
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-white/95 text-sm font-semibold tracking-wide">{card.label}</h3>
                </div>
                {card.is712 ? (
                  <div className="mt-2">
                    <p className="text-[11px] text-white/85 uppercase tracking-wider font-medium mb-2">{card.sub}</p>
                    <div className="flex gap-2">
                      <div className="flex-1 rounded-lg bg-amber-400/30 px-2 py-1.5 text-center">
                        <p className="text-white font-extrabold text-lg leading-none">{Number(card.ip712 || 0).toLocaleString()}</p>
                        <p className="text-white/80 text-[9px] font-bold uppercase mt-0.5">INPROCESS</p>
                      </div>
                      <div className="flex-1 rounded-lg bg-emerald-400/30 px-2 py-1.5 text-center">
                        <p className="text-white font-extrabold text-lg leading-none">{Number(card.done712 || 0).toLocaleString()}</p>
                        <p className="text-white/80 text-[9px] font-bold uppercase mt-0.5">DONE</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-4xl font-bold text-white tracking-tight mb-1">
                      {isPercent ? card.value : Number(card.value || 0).toLocaleString()}
                    </p>
                    <p className="text-[11px] text-white/85 uppercase tracking-wider font-medium">{card.sub}</p>
                  </div>
                )}
                {!card.is712 && badge && (
                  <div className={`absolute bottom-3 right-3 rounded-lg px-2.5 py-2 shadow-lg flex flex-col items-center gap-0.5 min-w-[60px] ${badge.above ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                    <svg width="36" height="16" viewBox="0 0 36 16" fill="none">
                      {badge.above ? (
                        <polyline points="2,13 10,8 18,10 26,4 34,2" stroke="#16a34a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                      ) : (
                        <polyline points="2,2 10,5 18,4 26,9 34,13" stroke="#dc2626" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                      )}
                    </svg>
                    <span className={`text-sm font-extrabold leading-none flex items-center gap-0.5 ${badge.above ? 'text-emerald-700' : 'text-red-700'}`}>
                      {badge.above ? (
                        <svg width="12" height="16" viewBox="0 0 12 16" fill="none">
                          <polyline points="9,14 3,10 7,7 1,3" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                          <polyline points="1,3 4,3 1,6" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                        </svg>
                      ) : (
                        <svg width="12" height="16" viewBox="0 0 12 16" fill="none">
                          <polyline points="3,2 9,6 5,9 11,13" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                          <polyline points="11,13 8,13 11,10" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                        </svg>
                      )}
                      {badge.pct}%
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
        {/* 10th tile: Insights */}
        <Card
          className="bg-gradient-to-br from-pink-400 via-violet-400 to-indigo-500 border-0 shadow-xl shadow-pink-400/30 hover:scale-[1.02] transition-transform duration-200"
          data-testid="kpi-insights"
        >
          <CardContent className="p-6 min-h-[150px] flex flex-col justify-between">
            <div className="flex items-center space-x-2">
              <div className="p-2.5 bg-white/20 rounded-lg backdrop-blur-sm">
                <Info className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-white/95 text-sm font-semibold tracking-wide">{insightTitle}</h3>
            </div>
            <div className="space-y-1.5">
              {(insightLines || []).map((ins, idx) => {
                const InsIcon = ins.icon;
                return (
                  <div key={idx} className={`flex items-center gap-1.5 text-[11px] ${ins.color}`}>
                    <InsIcon className="w-3 h-3 flex-shrink-0" />
                    <span className="leading-tight">{ins.text}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Overall Summary Chart */}
        <Card className="lg:col-span-2" data-testid="overall-summary-chart">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              OVERALL SUMMARY
            </CardTitle>
          </CardHeader>
          <CardContent>
            {overallSummaryData.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={overallSummaryData} barCategoryGap="20%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" fontSize={11} fontWeight={600} tick={{ fill: '#334155' }} />
                  <YAxis fontSize={10} tick={{ fill: '#64748b' }} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                  <Tooltip formatter={(value) => value.toLocaleString()} />
                  <Legend content={() => (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginTop: 4 }}>
                      {[{name:'REQ.', color:'#60a5fa'},{name:'DONE', color:'#34d399'},{name:'BAL', color:'#f87171'}].map(m => (
                        <div key={m.name} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
                          <div style={{ width: 12, height: 12, backgroundColor: m.color, borderRadius: 2 }} />
                          <span style={{ color: '#334155', fontWeight: 600 }}>{m.name}</span>
                        </div>
                      ))}
                    </div>
                  )} />
                  <Bar isAnimationActive={false} dataKey="REQ" fill="#1d4ed8" name="REQ." radius={[4,4,0,0]} label={{ position: 'top', fontSize: 10, fontWeight: 700, fill: '#1e3a8a', isAnimationActive: false}} />
                  <Bar isAnimationActive={false} dataKey="DONE" fill="#047857" name="DONE" radius={[4,4,0,0]} label={{ position: 'top', fontSize: 10, fontWeight: 700, fill: '#064e3b', isAnimationActive: false}} />
                  <Bar isAnimationActive={false} dataKey="BAL" fill="#b91c1c" name="BAL" radius={[4,4,0,0]} label={{ position: 'top', fontSize: 10, fontWeight: 700, fill: '#7f1d1d', isAnimationActive: false}} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center py-12 text-slate-400">
                Loading summary data...
              </div>
            )}
          </CardContent>
        </Card>

        {/* STATUS WISE Chart */}
        <Card data-testid="status-wise-chart">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <PieChartIcon className="w-5 h-5 text-amber-600" />
              STATUS WISE
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={statusChartData} barCategoryGap="25%">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" fontSize={11} fontWeight={600} tick={{ fill: '#334155' }} />
                <YAxis fontSize={10} tick={{ fill: '#64748b' }} />
                <Tooltip formatter={(value) => value.toLocaleString()} />
                <Bar isAnimationActive={false} dataKey="value" name="Count" radius={[4,4,0,0]}
                  label={{ position: 'top', fontSize: 11, fontWeight: 700, fill: '#0f172a', isAnimationActive: false}}>
                  {statusChartData.map((entry, i) => (
                    <Cell key={i} fill={STATUS_COLORS_CHART[entry.name] || '#94a3b8'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Status Overview Pie */}
        <Card className="bg-white border border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <PieChartIcon className="w-5 h-5 text-amber-600" />
              Status Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie isAnimationActive={false}
                    data={statusPieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {statusPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              {statusPercentages.map((item, idx) => (
                <div key={idx} className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                  <div>
                    <p className="text-xs font-medium text-slate-700">{item.name}</p>
                    <p className="text-sm font-bold text-slate-900">{item.percentage}%</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Status Wise Remark */}
        <Card className="lg:col-span-2" data-testid="status-wise-remark-chart">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-violet-600" />
              STATUS WISE REMARK
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={380}>
              <BarChart data={statusWiseRemarkData} barCategoryGap="8%">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="remark" fontSize={9} angle={-35} textAnchor="end" height={80} interval={0} tick={{ fill: '#334155', fontWeight: 600 }} />
                <YAxis fontSize={10} tick={{ fill: '#64748b' }} />
                <Tooltip formatter={(value) => value.toLocaleString()} labelFormatter={(label) => {
                  const item = statusWiseRemarkData.find(d => d.remark === label);
                  return `${label} (${item?.status || ''})`;
                }} />
                <Bar isAnimationActive={false} dataKey="count" name="Total" radius={[4,4,0,0]}
                  label={{ position: 'top', fontSize: 9, fontWeight: 700, fill: '#0f172a', isAnimationActive: false}}>
                  {statusWiseRemarkData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OverviewTab;
